import { useState, useEffect, useCallback, useMemo } from 'react';
import { digitalTwinApi } from '../../../Restclient/datacollection';
import { primeDashboardCache } from '../dashboard/hooks/useDTDashboard';

const DEFAULT_FILE_ID = 'default';

// Module-level cache: fileId → binding document.
// Survives component unmount/remount so switching back to the DT tab is instant.
// All write operations replace their entry in the cache with the fresh response.
const _bindingsCache = new Map();

/**
 * Manages the nested per-file component binding document stored in ConfigDigitalTwin.
 *
 * Document shape (new):
 * {
 *   id: fileId,
 *   file_id: fileId,
 *   file_name: "model.glb",
 *   components: {
 *     "mesh-id": { dashboard_id: string|null, label: string }
 *                  — null for unbound components
 *   },
 *   dashboardIndex: { [dashboardId]: [meshId, ...] }
 * }
 *
 * `getBinding(componentId)` returns the component entry directly; there is no
 * longer a `binding_id` alias — components are identified by their mesh ID.
 */
export function useComponentBindings(fileId, fileName) {
  const effectiveFileId = fileId || DEFAULT_FILE_ID;

  // Serve cached data immediately so the scene is interactive without waiting.
  const [fileDoc, setFileDoc] = useState(() => _bindingsCache.get(effectiveFileId) ?? null);
  const [loading, setLoading] = useState(!_bindingsCache.has(effectiveFileId));
  const [error, setError] = useState(null);

  const applyDoc = useCallback((doc) => {
    _bindingsCache.set(effectiveFileId, doc);
    setFileDoc(doc);
    // Pre-warm the dashboard cache for every dashboard referenced by this model
    // so clicking a component opens its dashboard without any additional wait.
    const dashIds = Object.keys(doc?.dashboardIndex || {});
    if (dashIds.length > 0) primeDashboardCache(dashIds);
  }, [effectiveFileId]);

  const fetchBindings = useCallback(async (background = false) => {
    if (!effectiveFileId) return;
    if (!background) setLoading(true);
    try {
      const res = await digitalTwinApi.getFileBindings(effectiveFileId);
      applyDoc(res.data || null);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        _bindingsCache.delete(effectiveFileId);
        setFileDoc(null);
      } else {
        setError(err.message || 'Failed to load bindings');
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [effectiveFileId, applyDoc]);

  useEffect(() => {
    if (_bindingsCache.has(effectiveFileId)) {
      // Show cached data immediately, then refresh silently in the background.
      fetchBindings(true);
    } else {
      fetchBindings(false);
    }
  }, [effectiveFileId, fetchBindings]);

  // The flat components map: { [componentId]: { dashboard_id, label } | null }
  const components = useMemo(() => fileDoc?.components || {}, [fileDoc]);

  // Reverse index: { [dashboardId]: [componentId, ...] }
  // Derived from the server-maintained dashboardIndex field; falls back to an
  // empty object so consumers always get a stable reference.
  const dashboardIndex = useMemo(() => fileDoc?.dashboardIndex || {}, [fileDoc]);

  /**
   * Returns the binding entry for a component: { dashboard_id, label } or null.
   */
  const getBinding = useCallback(
    (componentId) => components[componentId] || null,
    [components]
  );

  /**
   * Creates or updates the component entry with the given label.
   * Preserves the existing dashboard_id if already set.
   */
  const saveBinding = useCallback(
    async (componentId, label) => {
      const existing = components[componentId];
      const data = {
        label: label || componentId,
        dashboard_id: existing?.dashboard_id || null,
      };
      const res = await digitalTwinApi.setComponentBinding(
        effectiveFileId,
        fileName || effectiveFileId,
        componentId,
        data
      );
      applyDoc(res.data);
      return data;
    },
    [effectiveFileId, fileName, components, applyDoc]
  );

  /**
   * Updates only the dashboard_id on a single component entry.
   */
  const assignDashboard = useCallback(
    async (componentId, dashboardId) => {
      const res = await digitalTwinApi.updateComponentDashboard(
        effectiveFileId,
        componentId,
        dashboardId
      );
      applyDoc(res.data);
    },
    [effectiveFileId, applyDoc]
  );

  /**
   * Sets the same dashboard_id on multiple components in one backend write.
   */
  const batchAssignDashboard = useCallback(
    async (componentIds, dashboardId) => {
      const res = await digitalTwinApi.batchAssignDashboard(
        effectiveFileId,
        componentIds,
        dashboardId
      );
      applyDoc(res.data);
    },
    [effectiveFileId, applyDoc]
  );

  /**
   * Removes the component entry from the binding document.
   */
  const removeBinding = useCallback(
    async (componentId) => {
      const res = await digitalTwinApi.removeComponentBinding(effectiveFileId, componentId);
      applyDoc(res.data || null);
    },
    [effectiveFileId, applyDoc]
  );

  return {
    bindings: components,
    dashboardIndex,
    loading,
    error,
    getBinding,
    saveBinding,
    assignDashboard,
    batchAssignDashboard,
    removeBinding,
    refreshBindings: fetchBindings,
  };
}
